// locale-unextracted-ignore-file
import { useTimezone } from '@app/core/hooks/useTimezone';
import { useTzFormat } from '@app/core/hooks/useTzFormat';
import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { useLoadRangeAtStartTs } from '../../../../hooks/range/useLoadRangeAtStartTs';
import { useStreamUserDefinedRange } from '../../../../stores/stream-screen.store';
import { TimeChangeMeta, TimePicker } from './TimePicker';

export interface Props {
  isDisabled?: boolean;
}

/**
 * Time picker for the stream range. Uses stream range start (from) as the current
 * time; changing it builds a new timestamp in the station timezone and loads that range.
 */
export function TimeControl({ isDisabled = false }: Props) {
  const capture = usePostHogCapture();
  const tz = useTimezone();
  const formatTz = useTzFormat();
  const loadRangeAtTs = useLoadRangeAtStartTs();
  const { from, to } = useStreamUserDefinedRange();

  const onTimeChange = (timestamp: number, meta: TimeChangeMeta) => {
    if (!tz || from == null) {
      return;
    }

    capture('logger:time_changed', {
      old_time: formatTz(from, 'HH:mm:ss'),
      new_time: formatTz(timestamp, 'HH:mm:ss'),
      source: meta.source,
    });

    loadRangeAtTs(timestamp);
  };

  const toTime = DateTime.fromMillis(to, {
    zone: tz,
  }).toFormat('HH:mm');

  return (
    <TimePicker
      value={from}
      onChange={onTimeChange}
      disabled={isDisabled}
      timezone={tz ?? undefined}
      endAdornment={
        <Box
          color="text.disabled"
          sx={{
            opacity: isDisabled ? 0.5 : 1,
          }}
          fontSize={14}
        >
          → &nbsp;{toTime}
        </Box>
      }
      width="7ch"
    />
  );
}
